import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import {
  BookOpen,
  ChevronRight,
  Code2,
  Download,
  FileText,
  Film,
  Headset,
  History,
  MessageCircle,
  Mic,
  MonitorUp,
  Music,
  Paperclip,
  Send,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/stores/useAuthStore'
import { useWindowOpen } from '@/hooks/useWindowOpen'
import { shareSession } from '@/components/screenShare/session'
import { SCREEN_SHARE_NS } from '@/components/screenShare/strings'
import { useUIStore } from '@/stores/useUIStore'
import { cn } from '@/lib/utils'
import {
  buildSupportStreamUrl,
  createOrGetConversation,
  fetchMessages,
  fetchSupportStatus,
  rateConversation,
  sendMessage,
  fetchSupportConversations,
  reopenSupportConversation,
  supportSession,
  type SupportConversationSummary,
  uploadAttachment,
  type SupportAttachment,
  type SupportConversation,
  type SupportMessage,
  type SupportRating,
} from '@/lib/supportApi'
import { marketingUrl } from '@/lib/marketing'

const MAX_FILE_BYTES = 20 * 1024 * 1024
const ALLOWED_TYPES_HINT = 'image/*,video/*,audio/*,application/pdf,application/zip,.doc,.docx,.xls,.xlsx,.txt,.csv'

// Routes où le widget ne doit PAS s'afficher (paiement, redirects, sessions actives)
const HIDE_PATTERNS: RegExp[] = [
  /^\/(?:fr|en)\/checkout(?:\/|$)/,
  /^\/(?:fr|en)\/subscribe(?:\/|$)/,
  /^\/(?:fr|en)\/auth\/callback/,
  /^\/auth\/callback/,
  /^\/(?:fr|en)\/verify-email(?:\/|$)/,
  /^\/(?:fr|en)\/power-dialer\/session\//,
  // Transfert sur téléphone : le gros bouton Continuer occupe le bas de l'écran.
  /^\/(?:fr|en)\/transfert(?:\/|$)/,
  // Signature au téléphone par code QR : page publique, le bouton Signer occupe le bas de l'écran.
  /^\/(?:fr|en)\/signer\//,
]

// Vues du dashboard où le FAB recouvre des contrôles critiques (liste
// contacts, session power-dialer). La Boîte de réception ('phone', et l'ancien
// lien 'sms') n'est PAS dans le set : sans fil ouvert, le clavier et la liste
// ne sont pas gênés. Le cas fil ouvert est traité à part via useUIStore.
const HIDE_DASHBOARD_VIEWS = new Set(['power-dialer', 'conversations'])

function shouldHideDashboard(pathname: string, search: string): boolean {
  if (!/^\/(?:fr|en)\/dashboard\/?$/.test(pathname)) return false
  const view = new URLSearchParams(search).get('view') || 'phone'
  return HIDE_DASHBOARD_VIEWS.has(view)
}

// Boîte de réception avec un fil ouvert (onglet Conversations :
// selectedInboxPhone ; onglet Appels : selectedPhoneForSms). Le FAB support
// recouvrirait le composer ou la fiche → on masque.
function shouldHidePhoneSmsPanel(
  pathname: string,
  search: string,
  threads: { inbox: boolean; calls: boolean },
): boolean {
  if (!/^\/(?:fr|en)\/dashboard\/?$/.test(pathname)) return false
  const params = new URLSearchParams(search)
  const view = params.get('view') || 'phone'
  if (view !== 'phone' && view !== 'sms') return false
  return params.get('tab') === 'appels' ? threads.calls : threads.inbox
}

function shouldHideOnPath(pathname: string): boolean {
  return HIDE_PATTERNS.some((re) => re.test(pathname))
}

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type ViewState = 'closed' | 'menu' | 'email-capture' | 'chat' | 'history'

const SOUND_PREF_KEY = 'callizy_support_sound'
const STATUS_URL = 'https://status.callizy.ca'
const API_DOCS_URL = 'https://callizy.ca/docs'

/**
 * Jeton de reprise passé dans l'URL (?support=…), envoyé par email quand
 * l'équipe répond. Il rend sa conversation à un visiteur qui a changé
 * d'appareil ou perdu son stockage local.
 *
 * Consommé une fois puis retiré de la barre d'adresse : un jeton qui traîne
 * dans une URL finit copié dans un message ou un signet partagé.
 */
function consumeResumeToken(): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  const token = url.searchParams.get('support')
  if (!token) return null
  url.searchParams.delete('support')
  window.history.replaceState({}, '', url.toString())
  return token
}

/**
 * Une réponse non lue attend-elle le visiteur ?
 *
 * Vrai si le dernier message vient d'un agent et qu'il est postérieur à la
 * dernière consultation. Une session enregistrée avant l'introduction de
 * `seen_at` n'a pas de repère : on signale alors plutôt que de taire, une
 * pastille de trop coûtant moins cher qu'une réponse jamais vue.
 */
function hasUnseenAgentReply(
  messages: SupportMessage[],
  seenAt: string | undefined,
): boolean {
  const last = messages[messages.length - 1]
  if (!last || last.sender !== 'agent') return false
  if (!seenAt) return true
  return new Date(last.created_at).getTime() > new Date(seenAt).getTime()
}

interface Props {
  hidden?: boolean
}

export function SupportWidget({ hidden = false }: Props) {
  const { t } = useTranslation()
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  const user = useAuthStore((s) => s.user)
  const callsThreadActive = useUIStore((s) => s.selectedPhoneForSms !== null)
  const inboxThreadActive = useUIStore((s) => s.selectedInboxPhone !== null)
  // Une fenêtre ouverte (transfert, réglages, confirmation) a ses boutons en bas :
  // le bouton rond s'efface plutôt que de les couvrir, sur ordinateur comme sur téléphone.
  const windowOpen = useWindowOpen()

  const [view, setView] = useState<ViewState>('closed')
  const [conversation, setConversation] = useState<SupportConversation | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [guestEmail, setGuestEmail] = useState('')
  const [hasUnread, setHasUnread] = useState(false)
  const [history, setHistory] = useState<SupportConversationSummary[] | null>(null)
  const [teamOnline, setTeamOnline] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem(SOUND_PREF_KEY) !== '0'
  })

  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const esRef = useRef<EventSource | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const canSkipEmailCapture = isAuthenticated
  const firstName = user?.firstName?.trim()

  // ---- Sound ----
  const playPing = useCallback(() => {
    if (!soundEnabled || typeof window === 'undefined') return
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        audioCtxRef.current = new Ctx()
      }
      const ctx = audioCtxRef.current
      if (!ctx) return
      const now = ctx.currentTime
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(880, now)
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.08)
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35)
      osc.connect(gain).connect(ctx.destination)
      osc.start(now)
      osc.stop(now + 0.4)
    } catch {
      /* audio playback blocked, ignore */
    }
  }, [soundEnabled])

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev
      localStorage.setItem(SOUND_PREF_KEY, next ? '1' : '0')
      return next
    })
  }, [])

  // ---- Scroll ----
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }, [])

  // ---- Conversation ----
  const ensureConversation = useCallback(
    async (email?: string): Promise<SupportConversation | null> => {
      try {
        const conv = await createOrGetConversation({
          guestEmail: email,
          guestName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined,
        })
        setConversation(conv)
        const token = conv.session_token ?? supportSession.get()?.token
        if (token) {
          const payload = await fetchMessages(conv.id, token)
          setMessages(payload.messages)
        }
        return conv
      } catch (e) {
        console.error('[Support] init failed', e)
        toast.error(t('supportWidget.errorUnreachable'))
        return null
      }
    },
    [user?.firstName, user?.lastName],
  )

  /**
   * « Partager mon écran » : le partage est relié à la conversation du client
   * (créée au besoin), pour que l'équipe le voie dans le même sujet. Le
   * widget se ferme : la fenêtre d'accord passe devant, et le support voit la
   * page plutôt que le widget.
   */
  const shareScreen = useCallback(async () => {
    const conv = conversation ?? (await ensureConversation())
    setView('closed')
    shareSession.askConfirmation(conv?.id)
  }, [conversation, ensureConversation])

  /**
   * Reprise silencieuse au chargement de la page.
   *
   * Sans cela, rien ne se déclenchait avant un clic : un client qui revenait
   * après une réponse voyait la même bulle inerte. On ne reprend QUE sur un
   * jeton déjà stocké — appeler la création ici ouvrirait une conversation
   * vide, et un topic Telegram, à chaque chargement de page pour un client
   * connecté qui n'a rien demandé.
   */
  useEffect(() => {
    // Un lien de reprise l'emporte sur la session locale : c'est un retour
    // explicite du visiteur sur une conversation nommée.
    const resumeToken = consumeResumeToken()
    if (resumeToken) {
      const current = supportSession.get()
      if (current?.token !== resumeToken) {
        supportSession.set({ conv_id: '', token: resumeToken })
      }
    }
    const session = supportSession.get()
    if (!session?.token) return
    let cancelled = false
    void (async () => {
      try {
        const conv = await createOrGetConversation({})
        if (cancelled) return
        setConversation(conv)
        const payload = await fetchMessages(conv.id, conv.session_token ?? session.token)
        if (cancelled) return
        setMessages(payload.messages)
        if (resumeToken) setView('chat')
        else if (hasUnseenAgentReply(payload.messages, session.seen_at)) setHasUnread(true)
      } catch {
        // Jeton perime ou conversation close : la bulle reste inerte, le
        // clic reprendra le parcours normal.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Ouvrir la conversation VAUT lecture : la pastille s'eteint et le repere
  // avance, pour que la prochaine visite ne la rallume pas a tort.
  useEffect(() => {
    if (view === 'chat') {
      setHasUnread(false)
      supportSession.markSeen()
    }
  }, [view, messages.length])

  const openHistory = useCallback(async () => {
    setView('history')
    setHistory(null)
    try {
      setHistory(await fetchSupportConversations())
    } catch {
      setHistory([])
    }
  }, [])

  /**
   * Ouvre une conversation de l'historique. Une conversation résolue est
   * rouverte explicitement : sans cela le client ne pourrait ni écrire, ni
   * relire, et l'équipe recevrait un fil neuf sans le contexte précédent.
   */
  const openFromHistory = useCallback(
    async (summary: SupportConversationSummary) => {
      try {
        const conv =
          summary.status === 'resolved'
            ? await reopenSupportConversation(summary.id)
            : await createOrGetConversation({})
        setConversation(conv)
        const token = conv.session_token ?? supportSession.get()?.token
        if (token) {
          const payload = await fetchMessages(conv.id, token)
          setMessages(payload.messages)
        }
        setView('chat')
      } catch {
        toast.error(t('supportWidget.errorUnreachable'))
      }
    },
    [t],
  )

  const openWidget = useCallback(() => {
    setHasUnread(false)
    setView('menu')
  }, [])

  const openChat = useCallback(async () => {
    if (canSkipEmailCapture) {
      setView('chat')
      if (!conversation) await ensureConversation()
    } else {
      const existing = supportSession.get()
      if (existing) {
        setView('chat')
        if (!conversation) await ensureConversation()
      } else {
        setView('email-capture')
      }
    }
  }, [canSkipEmailCapture, conversation, ensureConversation])

  const closeWidget = useCallback(() => setView('closed'), [])

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const email = guestEmail.trim().toLowerCase()
      if (!email || !email.includes('@')) {
        toast.error(t('supportWidget.errorInvalidEmail'))
        return
      }
      const conv = await ensureConversation(email)
      if (conv) setView('chat')
    },
    [guestEmail, ensureConversation],
  )

  const pickFile = useCallback((file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(t('supportWidget.errorFileTooLarge', { mb: MAX_FILE_BYTES / (1024 * 1024) }))
      return
    }
    setPendingFile(file)
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file)
      setPendingPreviewUrl(url)
    } else {
      setPendingPreviewUrl(null)
    }
  }, [])

  const clearPendingFile = useCallback(() => {
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
    setPendingFile(null)
    setPendingPreviewUrl(null)
  }, [pendingPreviewUrl])

  const handleSend = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const content = input.trim()
      if (!conversation) return
      if (!content && !pendingFile) return
      if (sending) return
      const token = conversation.session_token ?? supportSession.get()?.token
      if (!token) {
        toast.error(t('supportWidget.errorSessionExpired'))
        return
      }
      setSending(true)
      const tempId = `tmp_${Date.now()}`
      const optimistic: SupportMessage = {
        id: tempId,
        sender: 'client',
        content,
        created_at: new Date().toISOString(),
        attachments: pendingFile
          ? [
              {
                id: `tmp_att_${tempId}`,
                kind: pendingFile.type.startsWith('image/')
                  ? 'image'
                  : pendingFile.type.startsWith('video/')
                  ? 'video'
                  : pendingFile.type.startsWith('audio/')
                  ? 'audio'
                  : 'document',
                mime: pendingFile.type || 'application/octet-stream',
                filename: pendingFile.name,
                size_bytes: pendingFile.size,
                url: pendingPreviewUrl ?? '',
              },
            ]
          : [],
      }
      setMessages((prev) => [...prev, optimistic])
      setInput('')
      const fileToSend = pendingFile
      const previewUrl = pendingPreviewUrl
      setPendingFile(null)
      setPendingPreviewUrl(null)
      scrollToBottom()
      try {
        const saved = fileToSend
          ? await uploadAttachment(conversation.id, token, fileToSend, content, (pct) => setUploadProgress(pct))
          : await sendMessage(conversation.id, token, content)
        setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)))
      } catch (err) {
        console.error('[Support] send failed', err)
        toast.error(t('supportWidget.errorSendFailed'))
        setMessages((prev) => prev.filter((m) => m.id !== tempId))
        setInput(content)
        if (fileToSend) {
          setPendingFile(fileToSend)
          setPendingPreviewUrl(previewUrl)
        }
      } finally {
        setSending(false)
        setUploadProgress(null)
      }
    },
    [input, sending, conversation, pendingFile, pendingPreviewUrl, scrollToBottom],
  )

  // ---- Status polling (toutes les 60s tant que le widget est monté) ----
  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      try {
        const s = await fetchSupportStatus()
        if (!cancelled) setTeamOnline(s.online)
      } catch {
        /* ignore */
      }
    }
    refresh()
    const interval = window.setInterval(refresh, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [])

  // ---- SSE ----
  useEffect(() => {
    if (!conversation) return
    const token = conversation.session_token ?? supportSession.get()?.token
    if (!token) return

    const es = new EventSource(buildSupportStreamUrl(conversation.id, token))
    esRef.current = es

    const onMessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as SupportMessage
        setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]))
        scrollToBottom()
        if (data.sender === 'agent') {
          playPing()
          setTeamOnline(true)
        }
        if (view !== 'chat') setHasUnread(true)
      } catch (err) {
        console.error('[Support SSE] parse error', err)
      }
    }
    const onStatus = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as { status: string }
        if (data.status === 'resolved') {
          setConversation((c) => (c ? { ...c, status: 'resolved' } : c))
          setMessages((prev) => [
            ...prev,
            {
              id: `sys_${Date.now()}`,
              sender: 'system',
              content: t('supportWidget.resolved'),
              created_at: new Date().toISOString(),
              attachments: [],
            },
          ])
        }
      } catch {
        /* ignore */
      }
    }

    es.addEventListener('support_message', onMessage)
    es.addEventListener('support_status', onStatus)
    es.onerror = () => {
      /* EventSource reconnects automatically with Last-Event-ID */
    }

    return () => {
      es.removeEventListener('support_message', onMessage)
      es.removeEventListener('support_status', onStatus)
      es.close()
      esRef.current = null
    }
  }, [conversation, scrollToBottom, view, playPing])

  useEffect(() => {
    if (view === 'chat') scrollToBottom()
  }, [view, messages.length, scrollToBottom])

  if (hidden) return null
  if (shouldHideOnPath(location.pathname)) return null
  if (shouldHideDashboard(location.pathname, location.search)) return null
  if (
    shouldHidePhoneSmsPanel(location.pathname, location.search, {
      inbox: inboxThreadActive,
      calls: callsThreadActive,
    })
  )
    return null

  const isEmptyChat = view === 'chat' && messages.length === 0
  const isResolved = conversation?.status === 'resolved'

  return (
    <>
      {/* FAB — bottom-right desktop, bottom-left mobile pour libérer la zone des boutons d'action */}
      <button
        type="button"
        aria-label={t('supportWidget.open')}
        onClick={openWidget}
        className={cn(
          'fixed bottom-5 left-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full shadow-lg shadow-black/20 transition-all',
          'sm:left-auto sm:right-5',
          // Sur le fond bleu nuit de l'onboarding et des forfaits, le bouton bleu nuit disparaissait : il passe en blanc.
          // Sur téléphone, dans l'onboarding, il cachait le bouton Continuer : il n'y apparaît pas.
          /^\/(?:fr|en)\/(?:onboarding|subscribe)\b/.test(location.pathname)
            ? cn(
                'bg-white text-[#0A192F] ring-1 ring-white/20 hover:scale-105 hover:shadow-xl',
                /^\/(?:fr|en)\/onboarding\b/.test(location.pathname) && 'max-sm:hidden',
              )
            : 'bg-primary text-primary-foreground hover:scale-105 hover:shadow-xl',
          (view !== 'closed' || windowOpen) && 'opacity-0 pointer-events-none scale-90',
        )}
      >
        <Headset className="h-5 w-5" />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-background" />
        )}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {view !== 'closed' && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className={cn(
              'fixed z-[60] flex flex-col overflow-hidden bg-background shadow-2xl shadow-black/30',
              'inset-0 h-full w-full',
              'sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[680px] sm:w-[400px] sm:max-h-[85vh] sm:max-w-[calc(100vw-2.5rem)] sm:rounded-2xl sm:border',
            )}
            onDragOver={(e) => {
              if (view === 'chat' && !isResolved) {
                e.preventDefault()
                setIsDragging(true)
              }
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragging(false)
              const file = e.dataTransfer.files?.[0]
              if (file && view === 'chat' && !isResolved) pickFile(file)
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b bg-gradient-to-br from-primary/10 to-transparent px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Headset className="h-5 w-5" />
                  </div>
                  <span
                    className={cn(
                      'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-background',
                      teamOnline ? 'bg-emerald-500' : 'bg-slate-400',
                    )}
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">{t('supportWidget.title')}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {teamOnline ? t('supportWidget.subtitleOnline') : t('supportWidget.subtitleOffline')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleSound}
                  aria-label={soundEnabled ? t('supportWidget.soundOn') : t('supportWidget.soundOff')}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                  title={soundEnabled ? t('supportWidget.soundOn') : t('supportWidget.soundOff')}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={closeWidget}
                  aria-label={t('supportWidget.close')}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            {view === 'history' && (
              <HistoryPanel
                conversations={history}
                onBack={() => setView('menu')}
                onOpen={openFromHistory}
              />
            )}

            {view === 'menu' && (
              <MenuPanel
                onChat={openChat}
                onHistory={isAuthenticated ? openHistory : undefined}
                onShareScreen={isAuthenticated ? () => void shareScreen() : undefined}
                teamOnline={teamOnline}
              />
            )}

            {view === 'email-capture' && (
              <form onSubmit={handleEmailSubmit} className="flex flex-1 flex-col justify-center gap-3 px-5">
                <p className="text-sm text-muted-foreground">
                  {t('supportWidget.emailIntro')}
                </p>
                <Input
                  type="email"
                  placeholder={t('supportWidget.emailPlaceholder')}
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  autoFocus
                  required
                />
                <Button type="submit">{t('supportWidget.emailStart')}</Button>
              </form>
            )}

            {view === 'chat' && (
              <>
                {isEmptyChat ? (
                  <HeroState online={teamOnline} firstName={firstName} />
                ) : (
                  <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
                    {messages.map((m) => (
                      <MessageBubble key={m.id} message={m} onImageClick={setLightboxUrl} />
                    ))}
                  </div>
                )}
                {isDragging && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-primary/10 backdrop-blur-sm">
                    <div className="rounded-2xl border-2 border-dashed border-primary px-6 py-4 text-sm font-medium text-primary">
                      {t('supportWidget.dropToSend')}
                    </div>
                  </div>
                )}

                {isResolved ? (
                  <RatingPanel
                    conversation={conversation}
                    onSubmitted={(rating) =>
                      setConversation((c) => (c ? { ...c, rating } : c))
                    }
                    onNewConversation={() => {
                      supportSession.clear()
                      setConversation(null)
                      setMessages([])
                      setView(canSkipEmailCapture ? 'chat' : 'email-capture')
                      if (canSkipEmailCapture) ensureConversation()
                    }}
                  />
                ) : (
                  <div className="border-t">
                    {isAuthenticated && <ShareScreenBar onShare={() => void shareScreen()} />}
                    {pendingFile && (
                      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 text-sm">
                        {pendingPreviewUrl && pendingFile.type.startsWith('image/') ? (
                          <img
                            src={pendingPreviewUrl}
                            alt={pendingFile.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                            <AttachmentIcon mime={pendingFile.type} />
                          </div>
                        )}
                        <div className="flex-1 truncate">
                          <div className="truncate text-xs font-medium">{pendingFile.name}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {humanFileSize(pendingFile.size)}
                            {uploadProgress != null && ` · envoi ${uploadProgress}%`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearPendingFile}
                          className="rounded-md p-1 text-muted-foreground hover:bg-accent"
                          aria-label={t('supportWidget.removeFile')}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    <form onSubmit={handleSend} className="flex items-center gap-2 px-3 py-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ALLOWED_TYPES_HINT}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) pickFile(file)
                          if (fileInputRef.current) fileInputRef.current.value = ''
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        aria-label={t('supportWidget.attachFile')}
                        disabled={sending}
                      >
                        <Paperclip className="h-4 w-4" />
                      </button>
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={isEmptyChat ? t('supportWidget.inputPlaceholderEmpty') : t('supportWidget.inputPlaceholder')}
                        disabled={sending || !conversation}
                        autoFocus
                      />
                      <Button
                        type="submit"
                        size="icon"
                        disabled={sending || (!input.trim() && !pendingFile)}
                        aria-label={t('supportWidget.send')}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </>
  )
}

function Lightbox({ url, onClose }: { url: string | null; onClose: () => void }) {
  const { t } = useTranslation()
  useEffect(() => {
    if (!url) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [url, onClose])

  if (!url) return null

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] flex animate-in fade-in items-center justify-center bg-black/90 p-4 duration-150"
    >
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <a
          href={url}
          download
          target="_blank"
          rel="noreferrer"
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
          aria-label={t('supportWidget.download')}
          title={t('supportWidget.download')}
        >
          <Download className="h-4 w-4" />
        </a>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
          aria-label={t('supportWidget.closeEsc')}
          title={t('supportWidget.closeEsc')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <img
        src={url}
        alt={t('supportWidget.preview')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95vh] max-w-[95vw] object-contain"
      />
    </div>
  )
}

/**
 * Historique des conversations d'un client connecté.
 *
 * Les données existaient et l'autorisation aussi : `_list_messages` accepte
 * déjà le propriétaire authentifié, sans exiger que la conversation soit
 * ouverte. Il ne manquait que le chemin pour y arriver.
 */
function HistoryPanel({
  conversations,
  onBack,
  onOpen,
}: {
  /** null = chargement en cours ; tableau vide = aucune conversation. */
  conversations: SupportConversationSummary[] | null
  onBack: () => void
  onOpen: (c: SupportConversationSummary) => void
}) {
  const { t, i18n } = useTranslation()
  const formatDate = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString(i18n.language, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : ''

  return (
    <div className="flex-1 overflow-y-auto p-3">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-xs text-muted-foreground hover:text-foreground"
      >
        ← {t('supportWidget.back')}
      </button>

      {conversations === null && (
        <p className="p-4 text-center text-sm text-muted-foreground">
          {t('supportWidget.historyLoading')}
        </p>
      )}

      {conversations?.length === 0 && (
        <p className="p-4 text-center text-sm text-muted-foreground">
          {t('supportWidget.historyEmpty')}
        </p>
      )}

      <div className="space-y-2">
        {conversations?.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onOpen(c)}
            className="w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {formatDate(c.last_message_at ?? c.created_at)}
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px]',
                  c.status === 'open'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-slate-500/10 text-muted-foreground',
                )}
              >
                {c.status === 'open'
                  ? t('supportWidget.historyOpen')
                  : t('supportWidget.historyResolved')}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-sm">
              {c.preview || t('supportWidget.historyNoMessage')}
            </p>
          </button>
        ))}
      </div>
    </div>
  )
}

/** Sous la conversation : partager son écran avec le support, en un clic. */
function ShareScreenBar({ onShare }: { onShare: () => void }) {
  const { t } = useTranslation(SCREEN_SHARE_NS)
  return (
    <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-1.5">
      <span className="truncate text-xs text-muted-foreground">{t('widget.subtitle')}</span>
      <button
        type="button"
        onClick={onShare}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-accent dark:text-blue-400"
      >
        <MonitorUp className="h-3.5 w-3.5" />
        {t('widget.title')}
      </button>
    </div>
  )
}

function MenuPanel({
  onChat,
  onHistory,
  onShareScreen,
  teamOnline,
}: {
  onChat: () => void
  /** Absent pour un visiteur anonyme : aucune identité à laquelle rattacher une liste. */
  onHistory?: () => void
  /** Absent pour un visiteur anonyme : seul un compte partage son écran. */
  onShareScreen?: () => void
  teamOnline: boolean
}) {
  const { t, i18n } = useTranslation()
  const { t: tShare } = useTranslation(SCREEN_SHARE_NS)
  // FAQ user (callizy.ca/{locale}/support), pas /docs qui est l'API technique.
  const helpUrl = marketingUrl('/support', i18n.language)
  // Le journal des nouveautés vit sur le site, dans le centre d'apprentissage
  const nouveautesUrl = marketingUrl('/apprendre/nouveautes', i18n.language)
  return (
    <div className="flex-1 space-y-3 overflow-y-auto p-3">
      {/* Action principale : chat en carte primaire pleine largeur — Intercom/Crisp pattern.
          Volontairement plus grosse et colorée que les autres items pour que l'œil
          tombe dessus en premier. */}
      <button
        type="button"
        onClick={onChat}
        className="group relative w-full overflow-hidden rounded-xl bg-blue-600 p-4 text-left text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/15">
            <MessageCircle className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">
                {t('supportWidget.menuChat')}
              </span>
              <span className="relative flex h-2 w-2">
                <span
                  className={cn(
                    'relative inline-flex h-2 w-2 rounded-full',
                    teamOnline ? 'bg-emerald-300' : 'bg-white/40',
                  )}
                />
                {teamOnline && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-70" />
                )}
              </span>
            </div>
            <div className="mt-0.5 text-xs text-white/80">
              {teamOnline
                ? t('supportWidget.subtitleOnline')
                : t('supportWidget.menuChatSubtitle')}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-white/80 transition-transform group-hover:translate-x-0.5" />
        </div>
      </button>
      <MenuItem
        icon={<span className="h-3 w-3 rounded-full bg-emerald-500" />}
        title={t('supportWidget.menuStatus')}
        subtitle={t('supportWidget.menuStatusSubtitleUnknown')}
        href={STATUS_URL}
        accentClass="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      />
      <MenuItem
        icon={<BookOpen className="h-5 w-5" />}
        title={t('supportWidget.menuHelp', 'Aide')}
        subtitle={t('supportWidget.menuHelpSubtitle', 'Questions fréquentes')}
        href={helpUrl}
        accentClass="bg-violet-500/10 text-violet-600 dark:text-violet-400"
      />
      <MenuItem
        icon={<Code2 className="h-5 w-5" />}
        title={t('supportWidget.menuApiDocs', 'Documentation API')}
        subtitle={t('supportWidget.menuApiDocsSubtitle', 'Pour développeurs')}
        href={API_DOCS_URL}
        accentClass="bg-slate-500/10 text-slate-600 dark:text-slate-400"
      />
      {onShareScreen && (
        <MenuItem
          icon={<MonitorUp className="h-5 w-5" />}
          title={tShare('widget.title')}
          subtitle={tShare('widget.subtitle')}
          onClick={onShareScreen}
          accentClass="bg-blue-500/10 text-blue-600 dark:text-blue-400"
        />
      )}
      {onHistory && (
        <MenuItem
          icon={<History className="h-5 w-5" />}
          title={t('supportWidget.menuHistory')}
          subtitle={t('supportWidget.menuHistorySubtitle')}
          onClick={onHistory}
          accentClass="bg-sky-500/10 text-sky-600 dark:text-sky-400"
        />
      )}
      <MenuItem
        icon={<Sparkles className="h-5 w-5" />}
        title={t('supportWidget.whatsNew', 'Quoi de neuf')}
        subtitle={t('supportWidget.whatsNewSubtitle', 'Dernières améliorations')}
        href={nouveautesUrl}
        accentClass="bg-amber-500/10 text-amber-600 dark:text-amber-400"
      />
    </div>
  )
}

function MenuItem({
  icon,
  title,
  subtitle,
  onClick,
  href,
  accentClass,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick?: () => void
  href?: string
  accentClass: string
}) {
  const inner = (
    <>
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', accentClass)}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{title}</div>
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </>
  )

  const baseClass = 'flex w-full items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-left transition-all hover:bg-accent hover:border-foreground/10'

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={baseClass}>
        {inner}
      </a>
    )
  }
  return (
    <button type="button" onClick={onClick} className={baseClass}>
      {inner}
    </button>
  )
}

function RatingPanel({
  conversation,
  onSubmitted,
  onNewConversation,
}: {
  conversation: SupportConversation | null
  onSubmitted: (rating: SupportRating) => void
  onNewConversation: () => void
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'ask' | 'feedback' | 'done'>(
    conversation?.rating ? 'done' : 'ask',
  )
  const [selectedRating, setSelectedRating] = useState<SupportRating | null>(null)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!conversation) return null

  const handleVote = async (rating: SupportRating) => {
    setSelectedRating(rating)
    setStep('feedback')
  }

  const submit = async (withFeedback: boolean) => {
    if (!selectedRating) return
    const token = conversation.session_token ?? supportSession.get()?.token
    if (!token) return
    setSubmitting(true)
    try {
      await rateConversation(conversation.id, token, selectedRating, withFeedback ? feedback : '')
      onSubmitted(selectedRating)
      setStep('done')
    } catch (e) {
      console.error('[Support] rating failed', e)
      toast.error(t('supportWidget.errorRatingFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'done') {
    return (
      <div className="space-y-2 border-t px-4 py-4 text-center text-xs text-muted-foreground">
        <div className="text-sm text-foreground">{t('supportWidget.thankYou')}</div>
        <button type="button" onClick={onNewConversation} className="underline">
          {t('supportWidget.startNew')}
        </button>
      </div>
    )
  }

  if (step === 'feedback' && selectedRating) {
    return (
      <div className="space-y-2 border-t px-4 py-3">
        <div className="text-center text-xs text-muted-foreground">
          {selectedRating === 'positive' ? t('supportWidget.rateThanksSuper') : t('supportWidget.rateThanksSorry')}
        </div>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={t('supportWidget.rateFeedbackPlaceholder')}
          rows={2}
          className="w-full resize-none rounded-md border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={submitting}
            className="text-xs text-muted-foreground hover:underline"
          >
            {t('supportWidget.skip')}
          </button>
          <Button type="button" size="sm" onClick={() => submit(true)} disabled={submitting}>
            {t('supportWidget.send')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2 border-t px-4 py-3 text-center">
      <div className="text-xs text-muted-foreground">
        {t('supportWidget.resolved')}
      </div>
      <div className="text-sm font-medium">{t('supportWidget.rateQuestion')}</div>
      <div className="flex items-center justify-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => handleVote('positive')}
          aria-label={t('supportWidget.ratePositive')}
          className="flex h-10 w-10 items-center justify-center rounded-full border bg-background transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 hover:border-emerald-500/40"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => handleVote('negative')}
          aria-label={t('supportWidget.rateNegative')}
          className="flex h-10 w-10 items-center justify-center rounded-full border bg-background transition-colors hover:bg-red-500/10 hover:text-red-600 hover:border-red-500/40"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={onNewConversation}
        className="text-[11px] text-muted-foreground hover:underline"
      >
        {t('supportWidget.rateSkip')}
      </button>
    </div>
  )
}

function AttachmentIcon({ mime }: { mime: string }) {
  if (mime.startsWith('image/')) return <FileText className="h-5 w-5" />
  if (mime.startsWith('video/')) return <Film className="h-5 w-5" />
  if (mime.startsWith('audio/')) return <Music className="h-5 w-5" />
  if (mime === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />
  return <FileText className="h-5 w-5" />
}

function HeroState({ online, firstName }: { online: boolean; firstName?: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className="relative mb-5"
      >
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/30">
          <Headset className="h-9 w-9" />
        </div>
        <span
          className={cn(
            'absolute -bottom-0.5 right-1 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-background',
            online ? 'bg-emerald-500' : 'bg-slate-400',
          )}
        >
          {online && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />}
        </span>
      </motion.div>
      <h2 className="text-xl font-semibold tracking-tight">
        {firstName ? t('supportWidget.heroGreetingNamed', { name: firstName }) : t('supportWidget.heroGreeting')}
      </h2>
      <p className="mt-1.5 text-sm text-muted-foreground">
        {t('supportWidget.heroSubtitle')}
      </p>
      {online && (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {t('supportWidget.teamOnlineBadge')}
        </div>
      )}
    </div>
  )
}

function MessageBubble({
  message,
  onImageClick,
}: {
  message: SupportMessage
  onImageClick: (url: string) => void
}) {
  const isClient = message.sender === 'client'
  const isSystem = message.sender === 'system'
  if (isSystem) {
    return <div className="my-2 text-center text-[11px] text-muted-foreground">{message.content}</div>
  }
  const hasAttachments = message.attachments && message.attachments.length > 0
  return (
    <div className={cn('flex', isClient ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] space-y-2 rounded-2xl text-sm',
          isClient ? 'rounded-br-sm bg-primary text-primary-foreground' : 'rounded-bl-sm bg-muted text-foreground',
          hasAttachments ? 'p-1.5' : 'px-3 py-2',
        )}
      >
        {hasAttachments &&
          message.attachments.map((att) => (
            <AttachmentBubble key={att.id} attachment={att} isClient={isClient} onImageClick={onImageClick} />
          ))}
        {message.content && (
          <div className={cn('whitespace-pre-wrap break-words', hasAttachments && 'px-2 pb-1')}>
            {message.content}
          </div>
        )}
      </div>
    </div>
  )
}

function AttachmentBubble({
  attachment,
  isClient,
  onImageClick,
}: {
  attachment: SupportAttachment
  isClient: boolean
  onImageClick: (url: string) => void
}) {
  if (attachment.kind === 'image') {
    return (
      <button
        type="button"
        onClick={() => attachment.url && onImageClick(attachment.url)}
        className="block overflow-hidden rounded-lg"
      >
        <img
          src={attachment.url}
          alt={attachment.filename}
          className="max-h-80 w-full cursor-zoom-in object-cover"
        />
      </button>
    )
  }
  if (attachment.kind === 'video') {
    return (
      <video
        controls
        preload="metadata"
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        className="max-h-80 w-full rounded-lg"
        src={attachment.url}
      >
        {/* fallback */}
      </video>
    )
  }
  if (attachment.kind === 'voice' || attachment.kind === 'audio') {
    return (
      <div className="flex items-center gap-2 rounded-lg px-2 py-1.5">
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-full', isClient ? 'bg-white/15' : 'bg-foreground/10')}>
          {attachment.kind === 'voice' ? <Mic className="h-4 w-4" /> : <Music className="h-4 w-4" />}
        </div>
        <audio
          controls
          preload="metadata"
          controlsList="nodownload noplaybackrate"
          className="h-8 flex-1"
          src={attachment.url}
        >
          {/* fallback */}
        </audio>
      </div>
    )
  }
  // document
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      download={attachment.filename}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
        isClient ? 'hover:bg-white/10' : 'hover:bg-foreground/5',
      )}
    >
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', isClient ? 'bg-white/15' : 'bg-foreground/10')}>
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{attachment.filename}</div>
        <div className="text-[10px] opacity-70">{humanFileSize(attachment.size_bytes)}</div>
      </div>
    </a>
  )
}
